/**
 * 🔍 VALIDATION HELPERS
 *
 * Type-safe validation and transformation functions that convert
 * our DTOs to Convex-compatible arguments.
 *
 * CONVEX BEST PRACTICE: "Use helper functions to write shared code"
 */
// ========================================
// 🔧 VALIDATION FUNCTIONS
// ========================================
/**
 * Validate and transform UpdateDomainRequestDTO to Convex arguments
 *
 * This function:
 * 1. Transforms field names (domainId → id)
 * 2. Validates required fields exist
 * 3. Returns Convex-compatible arguments
 *
 * @param dto The DTO from CLI/Web
 * @returns Validated Convex mutation arguments
 * @throws Error if validation fails
 */
export function validateUpdateDomainRequest(dto) {
    // ✅ FIELD NAME TRANSFORMATION
    const convexArgs = {
        id: dto.domainId, // ✅ Transform domainId → id with proper type
    };
    // ✅ COPY VALID FIELDS
    if (dto.tags !== undefined)
        convexArgs.tags = dto.tags;
    if (dto.binPriceEur !== undefined)
        convexArgs.binPriceEur = dto.binPriceEur;
    if (dto.binPriceUsd !== undefined)
        convexArgs.binPriceUsd = dto.binPriceUsd;
    if (dto.minOfferPrice !== undefined)
        convexArgs.minOfferPrice = dto.minOfferPrice;
    if (dto.rentToOwnEnabled !== undefined)
        convexArgs.rentToOwnEnabled = dto.rentToOwnEnabled;
    if (dto.registrar !== undefined)
        convexArgs.registrar = dto.registrar;
    if (dto.expirationAt !== undefined)
        convexArgs.expirationAt = dto.expirationAt;
    if (dto.autoRenew !== undefined)
        convexArgs.autoRenew = dto.autoRenew;
    // ✅ FIELD MAPPING: status field maps directly now
    if (dto.status !== undefined) {
        convexArgs.status = dto.status;
    }
    // ✅ NEW FIELD: notes
    if (dto.notes !== undefined) {
        convexArgs.notes = dto.notes;
    }
    return convexArgs;
}
/**
 * Validate and transform CreateDomainRequestDTO to Convex arguments
 */
export function validateCreateDomainRequest(dto) {
    // Validate required fields
    if (!dto.domain)
        throw new Error('Domain is required');
    if (!dto.userId)
        throw new Error('User ID is required');
    if (!dto.tld)
        throw new Error('TLD is required');
    const convexArgs = {
        domain: dto.domain,
        userId: dto.userId,
        tld: dto.tld,
        status: dto.status || 'unverified',
        rentToOwnEnabled: dto.rentToOwnEnabled ?? false,
    };
    // Copy optional fields
    if (dto.tags !== undefined)
        convexArgs.tags = dto.tags;
    if (dto.binPriceUsd !== undefined)
        convexArgs.binPriceUsd = dto.binPriceUsd;
    if (dto.binPriceEur !== undefined)
        convexArgs.binPriceEur = dto.binPriceEur;
    if (dto.minOfferPrice !== undefined)
        convexArgs.minOfferPrice = dto.minOfferPrice;
    if (dto.registrar !== undefined)
        convexArgs.registrar = dto.registrar;
    if (dto.expirationAt !== undefined)
        convexArgs.expirationAt = dto.expirationAt;
    if (dto.autoRenew !== undefined)
        convexArgs.autoRenew = dto.autoRenew;
    return convexArgs;
}
// ========================================
// 🚨 COMPILE-TIME TYPE SAFETY CHECKS
// ========================================
/**
 * These type checks will cause TypeScript errors if our DTOs
 * don't match the expected Convex argument types.
 */
// ❌ TYPE TESTS - These cause runtime errors, so they're commented out
// These SHOULD cause compile errors showing the mismatch:
// - Property 'id' is missing (we have 'domainId')  
// - Property 'domainId' does not exist in ConvexUpdateDomainArgs
// const _updateTypeTest: ConvexUpdateDomainArgs = validateUpdateDomainRequest({} as UpdateDomainRequestDTO);
// const _createTypeTest: ConvexCreateDomainArgs = validateCreateDomainRequest({} as CreateDomainRequestDTO);
